import React, { useState, useEffect, useCallback, useContext } from 'react';
import PropTypes from 'prop-types';
import LoginPopupSheet from '../../loginPopup/loginPopupSheet';
import { getRemainingDays } from '../../../utility/formator';
import { OrganizerUpdateView } from '@paypalcorp/donate-react';
import templateData from '../../../utility/getTemplateData';
import {
  handleLikeUpdate,
  addUpdateIdParameter,
} from '../organizerUpdatesUtils';
import { useHistory } from 'react-router';
import { isLoggedInUser, isOneTouchUser } from '../../../utility/authUtils';
import { MessageFormat, RelativeTimeFormat } from '@paypalcorp/worldready';
import { Context as WorldReadyContext } from '@paypalcorp/worldready-react';
import {
  trackUpdatesDetailIM,
  trackUpdatesLikePressed,
  trackUpdatesLoginDismissPressed,
} from '../../../fpti/fpti';

const UpdateView = ({
  update,
  setCurrentUpdate,
  showBadge = false,
  id,
  setShowLikeError,
  showLoading = false,
  fromUpdatesDetailedView = false,
}) => {
  const worldReady = useContext(WorldReadyContext);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoginSheetOpen, setLoginSheetOpen] = useState(false);
  const orgName = templateData.donationName ?? '';
  const campaignId = templateData.productId;
  const history = useHistory();

  const text = '[{"type":"p","children":[{"text":""}]}]';

  const organizerUpdatesContent = (route, params) => {
    return new MessageFormat(worldReady, {
      id: `common.organizerUpdates.${route}`,
    }).format(params);
  };

  const relativeTimeFormatter = new RelativeTimeFormat(worldReady, {
    timeZone: templateData.timezone,
    unit: 'day',
  });

  useEffect(() => {
    setIsLoading(showLoading);
  }, [showLoading]);

  useEffect(() => {
    if (update?.fou_id) {
      trackUpdatesDetailIM(update.fou_id);
    }
  }, []);

  const handleButtonClick = useCallback(async (updateId, isLiked) => {
    // this function receives the updateId and a boolean
    // indicating if the reaction is a like
    const currentLikesCount = update?.reactions?.[0]?.count ?? 0;
    trackUpdatesLikePressed(
      updateId,
      !isLiked,
      'REQUEST_SENT',
      currentLikesCount
    );
    setShowLikeError(false);
    if (!isLoggedInUser() && !isOneTouchUser()) {
      trackUpdatesLikePressed(
        updateId,
        !isLiked,
        'LOGIN_REQUIRED',
        currentLikesCount
      );
      addUpdateIdParameter(updateId, history.push);
      setLoginSheetOpen(true);
      return;
    }

    setIsLoading(true);
    try {
      const result = await handleLikeUpdate(isLiked, campaignId, updateId);
      if (result.ok) {
        trackUpdatesLikePressed(
          updateId,
          !isLiked,
          result.statusCode,
          !isLiked ? currentLikesCount + 1 : currentLikesCount - 1
        );
        setCurrentUpdate({ id: updateId, isLiked: !isLiked });
      } else {
        trackUpdatesLikePressed(
          updateId,
          !isLiked,
          'FAILED',
          currentLikesCount
        );
        setShowLikeError(true);
      }
      setIsLoading(false);
    } catch (err) {
      const error = JSON.parse(err.message);
      trackUpdatesLikePressed(
        updateId,
        !isLiked,
        error.statusCode,
        currentLikesCount
      );
      setIsLoading(false);
      setShowLikeError(true);
    }
  });

  const handleCloseSheet = () => {
    setLoginSheetOpen(false);
  };

  const onBeforeCloseSheet = (reason) => {
    trackUpdatesLoginDismissPressed(reason);
    return true;
  };

  return (
    <>
      <OrganizerUpdateView
        id={id}
        organizerUpdateContent={JSON.parse(update?.text ?? text)}
        organizerUpdateTitle={
          fromUpdatesDetailedView
            ? update?.subject
            : organizerUpdatesContent('updates.sectionHeading', {
                subject: update?.subject,
              })
        }
        organizerUpdateBadgeContent={organizerUpdatesContent('updates.badge')}
        date={new Date(update?.time_updated)}
        relativeTimeFormatter={relativeTimeFormatter}
        key={update?.fou_id}
        showNewBadge={
          showBadge
            ? getRemainingDays(new Date(update?.time_created)) <= 6
            : false
        }
        likeButtonProps={{
          onClick: () =>
            handleButtonClick(
              update?.fou_id,
              update?.reactions?.[0]?.currentUserReacted ?? false
            ),
          amount: update?.reactions?.[0]?.count ?? 0,
          isLiked: update?.reactions?.[0]?.currentUserReacted ?? false,
          btnState: isLoading ? 'processing' : 'initial',
          isReadOnly: false,
          content: organizerUpdatesContent(
            `updated.a11y.${
              update?.reactions?.[0]?.currentUserReacted ?? false ? '' : 'un'
            }selectedLike`,
            { number: update?.reactions?.[0]?.count ?? 0 }
          ),
        }}
        readMoreLabel={new MessageFormat(worldReady, {
          id: 'landing.campaigns.readMore',
        }).format()}
      />
      <LoginPopupSheet
        isSheetOpen={isLoginSheetOpen}
        closeSheet={handleCloseSheet}
        onBeforeCloseSheet={onBeforeCloseSheet}
        orgName={orgName}
      />
    </>
  );
};

UpdateView.propTypes = {
  update: PropTypes.object,
  organizerUpdatesContent: PropTypes.func,
  setCurrentUpdate: PropTypes.func,
  setShowLikeError: PropTypes.func,
  showBadge: PropTypes.bool,
  id: PropTypes.string,
  fromUpdatesDetailedView: PropTypes.bool,
};

export default UpdateView;
